import { ApiClient } from '../../../api-client';

import {
  formatAutoAssessmentsFilters,
  formatAutoAssessmentsRequestsDetailsResponse,
  formatAutoAssessmentsRequestsListResponse,
  formatAutoAssessmentsRetakeReasonsResponse,
} from './transformers';
import {
  IAssessmentsRetakeReasonsRawResponse,
  IAssessmentSubmissionRawResponse,
  IAssessmentUnlockRequestRawResponse,
  IAutoAssessmentsFilterResponse,
  IAutoAssessmentsFiltersPayload,
  IAutoAssessmentsRequestsListPayload,
  IAutoAssessmentsRequestsListRawResponse,
  IUpdateAssessmentRequestPayload,
  TAssessmentRequestDetailsRawResponse,
} from './types';
import {
  fetchAssessmentRequestDetailsUrl,
  fetchAssessmentRequestsUrl,
  fetchAssessmentsFiltersUrl,
  fetchRetakeReasonsUrl,
  unlockAssessmentRequestUrl,
  updateAssessmentRequestUrl,
} from './urls';

/**
 * This is provides the API client methods for all bids page,
 * filters dropdowns and components admin services like category, brands and
 * rank report etc.
 */
export class AutoAssessmentsAPIClient extends ApiClient {
  private static classInstance?: AutoAssessmentsAPIClient;

  private constructor() {
    super({
      apiBaseURL: 'ADMIN_SERVICE_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new AutoAssessmentsAPIClient();
    }

    return this.classInstance;
  };

  /**
   * Fetch all the leads data for the admin service.
   */
  public fetchAllAssessmentsRequests = async (
    { ...queryParams }: IAutoAssessmentsRequestsListPayload,
    identifier?: string
  ) => {
    const response = await this.post<IAutoAssessmentsRequestsListRawResponse>(
      fetchAssessmentRequestsUrl(),
      queryParams,
      {
        headers: {
          ...(identifier ? { 'x-device-identifier': identifier } : {}),
        },
      }
    );

    if (!response.success) throw response;

    return formatAutoAssessmentsRequestsListResponse(response.data.data);
  };

  /**
   * API call to fetch all the filters data for grade-pro assessments listing page.
   */
  public fetchAllAssessmentsFilters = async (
    payload: IAutoAssessmentsFiltersPayload,
    identifier?: string
  ) => {
    const response = await this.post<IAutoAssessmentsFilterResponse>(
      fetchAssessmentsFiltersUrl(),
      payload,
      {
        headers: {
          ...(identifier ? { 'x-device-identifier': identifier } : {}),
        },
      }
    );

    if (!response.success) throw response;

    return formatAutoAssessmentsFilters(response.data.data);
  };

  /**
   * API call to fetch the details of a particular assessment request.
   */
  public fetchAssessmentRequestDetails = async (
    sessionId: string,
    identifier?: string
  ) => {
    const response = await this.get<TAssessmentRequestDetailsRawResponse>(
      fetchAssessmentRequestDetailsUrl(sessionId),
      {
        headers: {
          ...(identifier ? { 'x-device-identifier': identifier } : {}),
        },
      }
    );

    if (!response.success) throw response;

    return formatAutoAssessmentsRequestsDetailsResponse(response.data);
  };

  /**
   * API call to fetch the retake reasons options.
   */
  public fetchRetakeReasons = async (identifier?: string) => {
    const response = await this.get<IAssessmentsRetakeReasonsRawResponse>(
      fetchRetakeReasonsUrl(),
      {
        headers: {
          ...(identifier ? { 'x-device-identifier': identifier } : {}),
        },
      }
    );

    if (!response.success) throw response;

    return formatAutoAssessmentsRetakeReasonsResponse(response.data.data);
  };

  /**
   * API call to submit/update the request for assessment.
   */
  public updateAssessmentRequest = async (
    payload: IUpdateAssessmentRequestPayload
  ) => {
    const { sessionId, identifier, ...rest } = payload;
    const localPayload = { ...rest };

    const response = await this.put<IAssessmentSubmissionRawResponse>(
      updateAssessmentRequestUrl(sessionId),
      localPayload,
      {
        headers: {
          ...(identifier ? { 'x-device-identifier': identifier } : {}),
        },
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to unlock the request.
   */
  public unlockAssessmentRequest = async (
    sessionId: string,
    identifier?: string
  ) => {
    const response = await this.patch<IAssessmentUnlockRequestRawResponse>(
      unlockAssessmentRequestUrl(sessionId),
      {},
      {
        headers: {
          ...(identifier ? { 'x-device-identifier': identifier } : {}),
        },
      }
    );

    if (!response.success) throw response;

    return response.data.message;
  };
}

export const AUTO_ASSESSMENTS_API =
  AutoAssessmentsAPIClient.getClientInstance();
